import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, RefreshCw, PenLine, Loader2, UserRound } from "lucide-react";
import { api, imgUrl, formatErr } from "@/lib/api";
import { FormatIcon } from "@/lib/content";
import { CardLimitModal } from "@/components/UpgradeModal";

const Choice = ({ on, onClick, children, testid, className = "" }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`panel text-left transition-colors ${on ? "border-[#9C4DDC] bg-[#F1E9FE]" : "hover:border-[#9C4DDC]/50"} ${className}`}>
    {children}
  </button>
);

function PickStep({ muses, formats, sel, setSel, onNext, busy }) {
  const ugc = sel.format_key === "product_ugc_ad";
  const ready = sel.muse_id && sel.format_key && (!ugc || (sel.product_name && sel.product_benefit && sel.product_url));
  const set = (k) => (e) => setSel({ ...sel, [k]: e.target.value });
  return (
    <div className="space-y-12">
      <section>
        <p className="label">1 · Choose a muse</p>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
          {muses.map((m) => (
            <Choice key={m.id} on={sel.muse_id === m.id} onClick={() => setSel({ ...sel, muse_id: m.id })} testid={`create-muse-option-${m.id}`} className="w-32 shrink-0 overflow-hidden">
              <div className="aspect-[4/5] bg-[#FAF7FE] flex items-center justify-center">
                {m.face_reference_url ? <img src={imgUrl(m.face_reference_url)} alt="" className="w-full h-full object-cover" /> : <UserRound className="text-[#CDBFE0]" size={40} />}
              </div>
              <div className="p-3"><p className="text-sm truncate">{m.name}</p>{m.is_example && <p className="text-[10px] muted uppercase tracking-wider">Example</p>}</div>
            </Choice>
          ))}
        </div>
      </section>
      <section>
        <p className="label">2 · Choose a format</p>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {formats.map((f) => (
            <Choice key={f.key} on={sel.format_key === f.key} onClick={() => setSel({ ...sel, format_key: f.key })} testid={`create-format-option-${f.key}`} className="p-4">
              <FormatIcon icon={f.icon} size={22} className="text-[#9C4DDC] mb-3" />
              <p className="font-display text-lg leading-tight">{f.name}</p>
              <p className="text-[10px] uppercase tracking-[0.15em] text-[#9C4DDC] mt-1">{f.purpose}</p>
              <p className="text-xs muted mt-2 leading-relaxed">{f.description}</p>
            </Choice>
          ))}
        </div>
      </section>
      <section className="space-y-5">
        <div>
          <p className="label">3 · Topic (optional)</p>
          <textarea className="field min-h-[90px]" value={sel.topic} onChange={set("topic")} placeholder="What should she talk about? Leave empty and we'll suggest" data-testid="create-topic-input" />
        </div>
        {ugc && (
          <div className="panel p-4 space-y-4" data-testid="create-product-fields">
            <div><label className="label">Product name *</label><input className="field" value={sel.product_name} onChange={set("product_name")} placeholder="e.g. Glow Prompt Pack" data-testid="create-product-name-input" /></div>
            <div><label className="label">Product benefit *</label><input className="field" value={sel.product_benefit} onChange={set("product_benefit")} placeholder="e.g. 50 prompts that keep your muse consistent" data-testid="create-product-benefit-input" /></div>
            <div><label className="label">Product URL *</label><input className="field" value={sel.product_url} onChange={set("product_url")} placeholder="e.g. https://stan.store/you/glow" data-testid="create-product-url-input" /></div>
          </div>
        )}
      </section>
      <div className="sticky bottom-20 md:bottom-6 z-30">
        <button className="btn-primary w-full py-4" disabled={!ready || busy} onClick={onNext} data-testid="create-get-ideas-button">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Finding ideas…</> : "Get 3 ideas"}
        </button>
      </div>
    </div>
  );
}

function IdeasStep({ ideas, pick, setPick, edits, setEdits, own, setOwn, onMore, onApprove, more, building }) {
  const quote = pick === "own" ? own : edits[pick];
  return (
    <div className="space-y-4">
      <p className="label">Approve the idea</p>
      {ideas.map((idea, i) => (
        <div key={i} onClick={() => setPick(i)} className={`panel p-5 cursor-pointer transition-colors ${pick === i ? "border-[#9C4DDC] bg-[#F1E9FE]" : "hover:border-[#9C4DDC]/50"}`} data-testid={`idea-option-${i}`}>
          <div className="flex items-start justify-between gap-3">
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#9C4DDC]">{idea.topic}</p>
            <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${pick === i ? "bg-[#9C4DDC] border-[#9C4DDC]" : "border-[#F0EAF7]"}`}>{pick === i && <Check size={12} className="text-white" />}</span>
          </div>
          {pick === i ? (
            <textarea className="field quote mt-3 min-h-[100px] text-base" value={edits[i]} onClick={(e) => e.stopPropagation()}
              onChange={(e) => setEdits(edits.map((q, j) => (j === i ? e.target.value : q)))} data-testid={`idea-quote-input-${i}`} />
          ) : (
            <p className="quote text-lg mt-2">“{edits[i]}”</p>
          )}
          <p className="text-xs muted mt-3">{idea.why_it_works}</p>
        </div>
      ))}
      <div onClick={() => setPick("own")} className={`panel p-5 cursor-pointer ${pick === "own" ? "border-[#9C4DDC] bg-[#F1E9FE]" : ""}`} data-testid="idea-option-own">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#9C4DDC] flex items-center gap-2"><PenLine size={12} /> Write my own</p>
        {pick === "own" && <textarea className="field mt-3 min-h-[90px]" autoFocus value={own} onChange={(e) => setOwn(e.target.value)} placeholder="Type the line she'll say or the headline" data-testid="idea-own-input" />}
      </div>
      <button className="btn-secondary w-full" onClick={onMore} disabled={more} data-testid="ideas-more-button">
        {more ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Give me 3 more
      </button>
      <div className="sticky bottom-20 md:bottom-6 z-30 pt-4">
        <button className="btn-primary w-full py-4" disabled={pick === null || !quote?.trim() || building} onClick={() => onApprove(quote)} data-testid="ideas-approve-button">
          {building ? <><Loader2 size={16} className="animate-spin" /> Building…</> : "Approve & build post"}
        </button>
      </div>
    </div>
  );
}

export default function Create() {
  const nav = useNavigate();
  const [muses, setMuses] = useState([]);
  const [formats, setFormats] = useState([]);
  const [usage, setUsage] = useState(null);
  const [sel, setSel] = useState({ muse_id: "", format_key: "", topic: "", product_name: "", product_benefit: "", product_url: "" });
  const [step, setStep] = useState("pick");
  const [ideas, setIdeas] = useState([]);
  const [seen, setSeen] = useState([]);
  const [edits, setEdits] = useState([]);
  const [pick, setPick] = useState(null);
  const [own, setOwn] = useState("");
  const [busy, setBusy] = useState(false);
  const [building, setBuilding] = useState(false);
  const [limit, setLimit] = useState(false);

  useEffect(() => {
    api.get("/muses").then(({ data }) => setMuses([...data.muses, ...(data.example ? [data.example] : [])]));
    api.get("/formats").then(({ data }) => setFormats(data));
    api.get("/usage").then(({ data }) => setUsage(data));
  }, []);

  const fetchIdeas = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/ideas", { ...sel, exclude: seen });
      setIdeas(data.ideas); setEdits(data.ideas.map((i) => i.quote)); setPick(null);
      setSeen((s) => [...s, ...data.ideas.map((i) => i.quote)]);
      setStep("ideas");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  const approve = async (quote) => {
    setBuilding(true);
    try {
      const topic = sel.topic || (typeof pick === "number" ? ideas[pick].topic : "");
      const { data } = await api.post("/cards", { ...sel, topic, quote });
      nav(`/library/${data.id}`);
    } catch (err) {
      if (["CARD_LIMIT_REACHED", "MONTHLY_CAP_REACHED"].includes(err?.response?.data?.detail)) setLimit(true);
      else toast.error(formatErr(err));
    } finally { setBuilding(false); }
  };

  return (
    <div className="max-w-3xl">
      {usage && usage.card_limit && (
        <div className={`panel px-4 py-3 mb-6 flex items-center justify-between text-sm ${usage.cards_used_total >= usage.card_limit ? "border-[#9C4DDC] bg-[#9C4DDC]/10 text-[#9C4DDC]" : ""}`} data-testid="create-free-plan-banner">
          <span>Free plan: {Math.min(usage.cards_used_total, usage.card_limit)} of {usage.card_limit} posts used</span>
          <Link to="/pricing" className="text-[11px] uppercase tracking-[0.15em] text-[#9C4DDC] underline-offset-4 hover:underline" data-testid="create-free-plan-upgrade-link">Upgrade</Link>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        {step === "ideas" ? (
          <button className="text-xs uppercase tracking-[0.15em] muted flex items-center gap-2" onClick={() => setStep("pick")} data-testid="create-back-button"><ArrowLeft size={14} /> Back</button>
        ) : <span />}

      </div>
      <div key={step} className="fade-up">
        <p className="eyebrow mb-2">{step === "pick" ? "Create content" : "Step 2 of 3"}</p>
        <h1 className="text-4xl sm:text-5xl mb-10">{step === "pick" ? "What are we making?" : "Pick her line"}</h1>
        {step === "pick" ? (
          <PickStep muses={muses} formats={formats} sel={sel} setSel={setSel} onNext={fetchIdeas} busy={busy} />
        ) : (
          <IdeasStep ideas={ideas} pick={pick} setPick={setPick} edits={edits} setEdits={setEdits} own={own} setOwn={setOwn}
            onMore={fetchIdeas} more={busy} onApprove={approve} building={building} />
        )}
      </div>
      <CardLimitModal open={limit} onOpenChange={setLimit} />
    </div>
  );
}
